import type { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { RootState } from '../store/store.state';
import { selectAuthToken } from './store/auth.store';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { InvalidateJWT, ValidateJWTRequest } from './store/auth.actions';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
    private ignoreIntercept: Array<string> = [
        '/api/configs',
        '/api/applications'
    ];

    constructor(
        private router: Router,
        private store: Store<RootState>
    ) {}

    waitForAuth(): Observable<string> {
        return this.store.pipe(
            select(selectAuthToken),
            filter((jwt) => jwt && jwt.length > 0)
        );
    }

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        if (this.ignoreIntercept.indexOf(req.url) > -1) {
            return next.handle(req);
        }

        // Wait for auth before making secure API calls
        return this.waitForAuth().pipe(
            take(1),
            switchMap((jwt) =>
                next.handle(this.setRequestAuthHeader(req, jwt))
            ),
            catchError((err) => {
                if (err instanceof HttpErrorResponse && err.status === 401) {
                    this.store.dispatch(new InvalidateJWT());
                    this.store.dispatch(new ValidateJWTRequest());
                    return this.waitForAuth().pipe(
                        take(1),
                        switchMap((jwt) =>
                            next.handle(this.setRequestAuthHeader(req, jwt))
                        )
                    );
                } else {
                    throw err;
                }
            })
        );
    }

    setRequestAuthHeader(req: HttpRequest<any>, jwt: string) {
        let processedReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${jwt}`)
        });

        return processedReq;
    }
}
